import { buildAnalyticsValueDisplay, formatAnalyticsRatio, resolveAnalyticsPrimaryBreakdownItem } from './optionsAnalyticsUtils';
import { formatPercent, getValueTone } from './optionsPerformanceUtils';

const buildResultText = (value, positiveText, negativeText, neutralText, unavailableText) => {
  if (value === null || value === undefined) return unavailableText;
  if (Number(value) > 0) return positiveText;
  if (Number(value) < 0) return negativeText;
  return neutralText;
};

export const buildAnalyticsMetricInfoMap = ({ metrics, performanceQuality, selectedCurrency, t, formatMessage }) => {
  const averageWinnerDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'average_winner_usd', nativeField: 'average_winner_native' });
  const averageLoserDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'average_loser_usd', nativeField: 'average_loser_native', absolute: true });
  const expectancyDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'expectancy_usd', nativeField: 'expectancy_native' });
  const feesDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'fees_usd', nativeField: 'fees_native', absolute: true });
  const grossDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'gross_trading_result_usd', nativeField: 'gross_trading_result_native', absolute: true });
  const grossProfitDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'gross_profit_usd', nativeField: 'gross_profit_native', absolute: true });
  const grossLossDisplay = buildAnalyticsValueDisplay({ metrics, selectedCurrency, usdField: 'gross_loss_usd_abs', nativeField: 'gross_loss_native_abs', absolute: true });
  const primaryBreakdown = resolveAnalyticsPrimaryBreakdownItem({ metrics, selectedCurrency });
  const expectancyValue = primaryBreakdown?.expectancy_native ?? metrics?.expectancy_usd;

  const maxDrawdownDisplay = selectedCurrency === 'usd'
    ? {
      amount: performanceQuality?.max_drawdown_usd === null || performanceQuality?.max_drawdown_usd === undefined ? '—' : buildAnalyticsValueDisplay({ metrics: { by_currency: [], max_drawdown_usd: performanceQuality?.max_drawdown_usd }, selectedCurrency: 'usd', usdField: 'max_drawdown_usd', nativeField: 'unused' }).primary,
      pct: formatPercent(performanceQuality?.max_drawdown_pct),
    }
    : {
      amount: performanceQuality?.max_drawdown_native === null || performanceQuality?.max_drawdown_native === undefined
        ? '—'
        : buildAnalyticsValueDisplay({
          metrics: {
            max_drawdown_usd: performanceQuality?.max_drawdown_usd,
            by_currency: primaryBreakdown ? [{
              currency: primaryBreakdown.currency,
              max_drawdown_native: performanceQuality?.max_drawdown_native,
            }] : [],
          },
          selectedCurrency,
          usdField: 'max_drawdown_usd',
          nativeField: 'max_drawdown_native',
        }).primary,
      pct: formatPercent(performanceQuality?.max_drawdown_pct),
    };

  const feeImpactBreakdown = [
    { label: t('analytics.metricInfo.feeImpact.breakdownFees'), value: feesDisplay.primary, note: feesDisplay.secondary || null },
    { label: t('analytics.metricInfo.feeImpact.breakdownGross'), value: grossDisplay.primary, note: grossDisplay.secondary || null },
    { label: t('analytics.metricInfo.feeImpact.breakdownImpact'), value: formatPercent(metrics?.fee_impact_pct), toneClass: getValueTone(-(metrics?.fee_impact_pct || 0)) },
  ];

  return {
    profitFactor: {
      title: t('analytics.profitFactor'),
      description: t('analytics.metricInfo.shared.readingGuide'),
      sections: [
        { heading: t('analytics.metricInfo.sections.whatIs'), body: t('analytics.metricInfo.profitFactor.whatIs') },
        { heading: t('analytics.metricInfo.sections.howCalculated'), body: t('analytics.metricInfo.profitFactor.howCalculated'), formula: t('analytics.metricInfo.profitFactor.formula'), breakdown: [
          { label: t('analytics.metricInfo.profitFactor.breakdownGrossProfit'), value: grossProfitDisplay.primary, note: grossProfitDisplay.secondary || null },
          { label: t('analytics.metricInfo.profitFactor.breakdownGrossLoss'), value: grossLossDisplay.primary, note: grossLossDisplay.secondary || null },
        ] },
        { heading: t('analytics.metricInfo.sections.howInterpret'), body: t('analytics.metricInfo.profitFactor.howInterpret') },
        {
          heading: t('analytics.metricInfo.sections.yourResult'),
          body: metrics?.profit_factor === null || metrics?.profit_factor === undefined
            ? t('analytics.metricInfo.profitFactor.resultUnavailable')
            : formatMessage(t('analytics.metricInfo.profitFactor.resultTemplate'), { value: formatAnalyticsRatio(metrics?.profit_factor) }),
          note: metrics?.profit_factor === null || metrics?.profit_factor === undefined
            ? t('analytics.metricInfo.profitFactor.resultUnavailableNote')
            : metrics.profit_factor < 1
              ? t('analytics.metricInfo.profitFactor.resultBelowOne')
              : metrics.profit_factor === 1
                ? t('analytics.metricInfo.profitFactor.resultAtOne')
                : t('analytics.metricInfo.profitFactor.resultAboveOne'),
        },
      ],
    },
    expectancy: {
      title: t('analytics.expectancy'),
      description: t('analytics.metricInfo.shared.readingGuide'),
      sections: [
        { heading: t('analytics.metricInfo.sections.whatIs'), body: t('analytics.metricInfo.expectancy.whatIs') },
        { heading: t('analytics.metricInfo.sections.howCalculated'), body: t('analytics.metricInfo.expectancy.howCalculated'), formula: t('analytics.metricInfo.expectancy.formula'), breakdown: [
          { label: t('analytics.winRate'), value: formatPercent(metrics?.win_rate) },
          { label: t('analytics.averageWinner'), value: averageWinnerDisplay.primary, note: averageWinnerDisplay.secondary || null },
          { label: t('analytics.averageLoser'), value: averageLoserDisplay.primary, note: averageLoserDisplay.secondary || null },
        ], note: t('analytics.metricInfo.expectancy.unitNote') },
        { heading: t('analytics.metricInfo.sections.howInterpret'), body: t('analytics.metricInfo.expectancy.howInterpret') },
        {
          heading: t('analytics.metricInfo.sections.yourResult'),
          body: expectancyValue === null || expectancyValue === undefined
            ? t('analytics.metricInfo.expectancy.resultUnavailable')
            : formatMessage(t('analytics.metricInfo.expectancy.resultTemplate'), { value: expectancyDisplay.primary }),
          note: buildResultText(
            expectancyValue,
            t('analytics.metricInfo.expectancy.resultPositive'),
            t('analytics.metricInfo.expectancy.resultNegative'),
            t('analytics.metricInfo.expectancy.resultNeutral'),
            t('analytics.metricInfo.expectancy.resultUnavailable')
          ),
        },
      ],
    },
    payoffRatio: {
      title: t('analytics.payoffRatio'),
      description: t('analytics.metricInfo.shared.readingGuide'),
      sections: [
        { heading: t('analytics.metricInfo.sections.whatIs'), body: t('analytics.metricInfo.payoffRatio.whatIs') },
        { heading: t('analytics.metricInfo.sections.howCalculated'), body: t('analytics.metricInfo.payoffRatio.howCalculated'), formula: t('analytics.metricInfo.payoffRatio.formula'), breakdown: [
          { label: t('analytics.averageWinner'), value: averageWinnerDisplay.primary, note: averageWinnerDisplay.secondary || null },
          { label: t('analytics.averageLoser'), value: averageLoserDisplay.primary, note: averageLoserDisplay.secondary || null },
        ] },
        { heading: t('analytics.metricInfo.sections.howInterpret'), body: t('analytics.metricInfo.payoffRatio.howInterpret') },
        {
          heading: t('analytics.metricInfo.sections.yourResult'),
          body: metrics?.payoff_ratio === null || metrics?.payoff_ratio === undefined
            ? t('analytics.metricInfo.payoffRatio.resultUnavailable')
            : formatMessage(t('analytics.metricInfo.payoffRatio.resultTemplate'), { value: formatAnalyticsRatio(metrics?.payoff_ratio) }),
          note: metrics?.payoff_ratio === null || metrics?.payoff_ratio === undefined
            ? t('analytics.metricInfo.payoffRatio.resultUnavailable')
            : metrics.payoff_ratio >= 1
              ? t('analytics.metricInfo.payoffRatio.resultAboveOne')
              : t('analytics.metricInfo.payoffRatio.resultBelowOne'),
        },
      ],
    },
    premiumCaptured: {
      title: t('analytics.premiumCaptured'),
      description: t('analytics.metricInfo.shared.readingGuide'),
      sections: [
        { heading: t('analytics.metricInfo.sections.whatIs'), body: t('analytics.metricInfo.premiumCaptured.whatIs') },
        { heading: t('analytics.metricInfo.sections.howCalculated'), body: t('analytics.metricInfo.premiumCaptured.howCalculated'), formula: t('analytics.metricInfo.premiumCaptured.formula'), note: t('analytics.metricInfo.premiumCaptured.eligibilityNote') },
        { heading: t('analytics.metricInfo.sections.howInterpret'), body: t('analytics.metricInfo.premiumCaptured.howInterpret') },
        {
          heading: t('analytics.metricInfo.sections.yourResult'),
          body: metrics?.premium_captured_pct === null || metrics?.premium_captured_pct === undefined
            ? t('analytics.metricInfo.premiumCaptured.resultUnavailable')
            : formatMessage(t('analytics.metricInfo.premiumCaptured.resultTemplate'), {
              value: formatPercent(metrics?.premium_captured_pct),
              count: metrics?.premium_captured_sample_count || 0,
            }),
          note: metrics?.premium_captured_pct === null || metrics?.premium_captured_pct === undefined
            ? t('analytics.metricInfo.premiumCaptured.resultUnavailable')
            : t('analytics.metricInfo.premiumCaptured.resultNote'),
        },
      ],
    },
    feeImpact: {
      title: t('analytics.feeImpact'),
      description: t('analytics.metricInfo.shared.readingGuide'),
      sections: [
        { heading: t('analytics.metricInfo.sections.whatIs'), body: t('analytics.metricInfo.feeImpact.whatIs') },
        { heading: t('analytics.metricInfo.sections.howCalculated'), body: t('analytics.metricInfo.feeImpact.howCalculated'), formula: t('analytics.metricInfo.feeImpact.formula'), breakdown: feeImpactBreakdown },
        { heading: t('analytics.metricInfo.sections.howInterpret'), body: t('analytics.metricInfo.feeImpact.howInterpret') },
        {
          heading: t('analytics.metricInfo.sections.yourResult'),
          body: metrics?.fee_impact_pct === null || metrics?.fee_impact_pct === undefined
            ? t('analytics.metricInfo.feeImpact.resultUnavailable')
            : formatMessage(t('analytics.metricInfo.feeImpact.resultTemplate'), {
              impact: formatPercent(metrics?.fee_impact_pct),
              fees: feesDisplay.primary,
              gross: grossDisplay.primary,
            }),
          note: metrics?.fee_impact_pct === null || metrics?.fee_impact_pct === undefined
            ? t('analytics.metricInfo.feeImpact.resultUnavailableNote')
            : t('analytics.metricInfo.feeImpact.resultNote'),
        },
      ],
    },
    maxDrawdown: {
      title: t('analytics.maxDrawdown'),
      description: t('analytics.metricInfo.shared.readingGuide'),
      sections: [
        { heading: t('analytics.metricInfo.sections.whatIs'), body: t('analytics.metricInfo.maxDrawdown.whatIs') },
        { heading: t('analytics.metricInfo.sections.howCalculated'), body: t('analytics.metricInfo.maxDrawdown.howCalculated'), formula: t('analytics.metricInfo.maxDrawdown.formula'), note: t('analytics.metricInfo.maxDrawdown.snapshotNote') },
        { heading: t('analytics.metricInfo.sections.howInterpret'), body: t('analytics.metricInfo.maxDrawdown.howInterpret') },
        {
          heading: t('analytics.metricInfo.sections.yourResult'),
          body: performanceQuality?.max_drawdown_pct === null || performanceQuality?.max_drawdown_pct === undefined
            ? t('analytics.metricInfo.maxDrawdown.resultUnavailable')
            : formatMessage(t('analytics.metricInfo.maxDrawdown.resultTemplate'), {
              pct: formatPercent(performanceQuality?.max_drawdown_pct),
              amount: maxDrawdownDisplay.amount,
            }),
          note: performanceQuality?.max_drawdown_pct === null || performanceQuality?.max_drawdown_pct === undefined
            ? t('analytics.metricInfo.maxDrawdown.resultUnavailableNote')
            : t('analytics.metricInfo.maxDrawdown.resultNote'),
        },
      ],
    },
  };
};

export default buildAnalyticsMetricInfoMap;